import { useEffect, useRef, useState } from 'react';

// Typed (handwriting-font) signature: the signer types their name and picks a
// script font; we render it to a canvas and emit a PNG data URL so it flows
// through exactly the same path as a drawn/saved signature. Kept self-contained
// (own font list + loader) so it can be reused on the public sign page and in
// signature settings without cross-imports.

const FONT_CHOICES: Array<{ id: string; label: string; family: string; size: number }> = [
  { id: 'dancing', label: 'Dancing Script', family: '"Dancing Script", cursive', size: 56 },
  { id: 'great', label: 'Great Vibes', family: '"Great Vibes", cursive', size: 64 },
  { id: 'sacramento', label: 'Sacramento', family: '"Sacramento", cursive', size: 60 },
  { id: 'caveat', label: 'Caveat', family: '"Caveat", cursive', size: 64 },
];

const FONT_LINK_ID = 'esd-handwriting-fonts';
function ensureHandwritingFontsLoaded(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Sacramento&display=swap';
  document.head.appendChild(link);
}

export function FontSignaturePad({
  defaultText = '',
  placeholder,
  onChange,
}: {
  defaultText?: string;
  placeholder?: string;
  onChange: (dataUrl: string) => void;
}) {
  const [text, setText] = useState(defaultText);
  const [fontId, setFontId] = useState(FONT_CHOICES[0].id);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    ensureHandwritingFontsLoaded();
  }, []);

  const choice = FONT_CHOICES.find((f) => f.id === fontId) ?? FONT_CHOICES[0];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${choice.size}px ${choice.family}`;
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text || ' ', canvas.width / 2, canvas.height / 2);
      // Emit only when there's real text; empty keeps the field invalid so the
      // signer can't submit a blank typed signature.
      onChange(text.trim() ? canvas.toDataURL('image/png') : '');
    };
    if ('fonts' in document) {
      document.fonts.load(`${choice.size}px ${choice.family}`).then(draw).catch(draw);
    } else {
      draw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, choice.family, choice.size]);

  return (
    <div>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={60}
        placeholder={placeholder}
        className="input mb-2 text-[14px]"
      />
      <div className="flex flex-wrap gap-1.5 mb-2">
        {FONT_CHOICES.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFontId(f.id)}
            className={`px-2.5 py-1 rounded-md text-[13px] border ${
              f.id === fontId ? 'border-primary bg-primary-50' : 'border-border bg-white'
            }`}
            style={{ fontFamily: f.family }}
          >
            {text.trim() ? text.slice(0, 14) : f.label}
          </button>
        ))}
      </div>
      <div className="border border-border rounded-md bg-white inline-block max-w-full">
        <canvas ref={canvasRef} width={420} height={120} className="max-w-full" />
      </div>
    </div>
  );
}
